import React, { useMemo } from "react";
import * as echarts from "echarts";
import ScreeningPolicyCard from "./ScreeningPolicyCard";

/* =========================
   Minimal ECharts Card
   ========================= */

function useEchart(option) {
  const ref = React.useRef(null);
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // init once (no theme applied - keep default gray background)
    if (!chartRef.current) {
      chartRef.current = echarts.init(el, undefined, { renderer: "canvas" });
    }

    // apply option
    if (option)
      chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });

    // ensure first paint matches container size
    requestAnimationFrame(() => chartRef.current?.resize());

    // resize on window + container size changes
    const handle = () => chartRef.current?.resize();
    window.addEventListener("resize", handle);
    const ro = new ResizeObserver(handle);
    ro.observe(el);

    return () => {
      window.removeEventListener("resize", handle);
      ro.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []); // init

  React.useEffect(() => {
    if (chartRef.current && option) {
      chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });
      requestAnimationFrame(() => chartRef.current?.resize());
    }
  }, [option]);

  return ref;
}

function ChartCard({ option }) {
  const ref = useEchart(option);
  return (
    <div className="chart-card">
      <div ref={ref} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

/* =========================
   Dataset IDs (as provided)
   ========================= */
const AROPE_ID = "68c95f773726a28c8c93e5fd";
const AGE_ID = "68c95f77df308a17d7d15319";
const HCE_ID = "68c95f773e0f2bd2969e17c2";
const IMM_ID = "68c95f77beac5ab13cc64d97";

/* =========================
   Helpers
   ========================= */
function applyTextColor(option) {
  if (!option) return option;
  const isDark = document.body.getAttribute('data-theme') === 'dark';
  const textColor = isDark ? '#e0e0e0' : '#333';

  return {
    ...option,
    title: option.title ? { ...option.title, textStyle: { ...option.title.textStyle, color: textColor } } : undefined,
    legend: option.legend ? { ...option.legend, textStyle: { ...option.legend.textStyle, color: textColor } } : undefined,
    xAxis: option.xAxis ? (Array.isArray(option.xAxis)
      ? option.xAxis.map(axis => ({ ...axis, axisLabel: { ...axis.axisLabel, color: textColor }, nameTextStyle: { ...axis.nameTextStyle, color: textColor } }))
      : { ...option.xAxis, axisLabel: { ...option.xAxis.axisLabel, color: textColor }, nameTextStyle: { ...option.xAxis.nameTextStyle, color: textColor } })
      : undefined,
    yAxis: option.yAxis ? (Array.isArray(option.yAxis)
      ? option.yAxis.map(axis => ({ ...axis, axisLabel: { ...axis.axisLabel, color: textColor }, nameTextStyle: { ...axis.nameTextStyle, color: textColor } }))
      : { ...option.yAxis, axisLabel: { ...option.yAxis.axisLabel, color: textColor }, nameTextStyle: { ...option.yAxis.nameTextStyle, color: textColor } })
      : undefined,
  };
}

function getElements(metas, dataById, id) {
  // getDataset(...) payload stored in HomePage as detailsById[id]
  // shape: { id, datasetName, ..., elements: [...] }
  const payload = dataById?.[id];
  return payload?.elements || [];
}

function yearColumnsFromFirstRow(rows) {
  const row = rows?.[0] || {};
  return Object.keys(row)
    .filter((k) => /^\d{4}$/.test(String(k)))
    .sort(); // "2019"..."2024"
}

function filterByType(rows, type) {
  return (rows || []).filter((r) => (r?.["Demo Group Type"] || "") === type);
}

function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/* =========================
   AROPE charts
   ========================= */
function aropeByNUTS2(rows) {
  const r = filterByType(rows, "NUTS-2");
  if (!r.length) return null;
  const years = yearColumnsFromFirstRow(r);

  const series = r.map((row) => ({
    name: row["Demographic Group"],
    type: "line",
    smooth: true,
    data: years.map((y) => num(row[y])),
  }));

  return {
    title: { text: "AROPE by NUTS-2 Level", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    legend: { type: "scroll", top: 28 },
    grid: { left: 56, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series,
  };
}

function aropeByGender(rows) {
  const r = filterByType(rows, "Gender");
  if (!r.length) return null;
  const years = yearColumnsFromFirstRow(r);
  const menRow = r.find(
    (x) => String(x["Demographic Group"]).toLowerCase() === "men"
  );
  const womenRow = r.find(
    (x) => String(x["Demographic Group"]).toLowerCase() === "women"
  );

  return {
    title: { text: "AROPE by Gender", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    legend: { top: 28 },
    grid: { left: 56, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series: [
      {
        name: "Men",
        type: "line",
        smooth: true,
        data: years.map((y) => num(menRow?.[y])),
      },
      {
        name: "Women",
        type: "line",
        smooth: true,
        data: years.map((y) => num(womenRow?.[y])),
      },
    ],
  };
}

function aropeByAge(rows) {
  const r = filterByType(rows, "Age");
  if (!r.length) return null;
  const years = yearColumnsFromFirstRow(r);

  const series = r.map((row) => ({
    name: row["Demographic Group"],
    type: "line",
    smooth: true,
    data: years.map((y) => num(row[y])),
  }));

  return {
    title: { text: "AROPE by Age Category", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    legend: { type: "scroll", top: 28 },
    grid: { left: 56, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series,
  };
}

function aropeByBirth(rows) {
  const r = filterByType(rows, "Country of Birth");
  if (!r.length) return null;
  const years = yearColumnsFromFirstRow(r);

  const series = r.map((row) => ({
    name: row["Demographic Group"],
    type: "line",
    smooth: true,
    data: years.map((y) => num(row[y])),
  }));

  return {
    title: { text: "AROPE by Country of Birth", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    legend: { top: 28 },
    grid: { left: 56, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series,
  };
}

function aropeByUrbanicity(rows) {
  const r = filterByType(rows, "Urbanicity");
  if (!r.length) return null;
  const years = yearColumnsFromFirstRow(r);

  const series = r.map((row) => ({
    name: row["Demographic Group"],
    type: "line",
    smooth: true,
    data: years.map((y) => num(row[y])),
  }));

  return {
    title: { text: "AROPE by Population Density", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    legend: { top: 28 },
    grid: { left: 56, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series,
  };
}

/* =========================
   Age Distribution pyramids
   ========================= */
function agePyramid(elements, demogLabel, title) {
  // Rows have positive "Women" and *negative* "Men", with "Men [Original]" positive if you need it
  const rows = (elements || []).filter(
    (r) =>
      String(r["Demographic Group"] || "").toLowerCase() ===
      demogLabel.toLowerCase()
  );
  if (!rows.length) return null;

  const ages = rows.map((r) => String(r["Age Group"]));
  const yCats = [...ages].reverse();
  const men = rows.map((r) => num(r["Men"])).reverse(); // already negative
  const women = rows.map((r) => num(r["Women"])).reverse(); // positive

  return {
    title: { text: title, left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { top: 28 },
    grid: { left: 88, right: 48, top: 72, bottom: 24 },
    xAxis: [
      {
        type: "value",
        axisLabel: { formatter: (v) => Math.abs(v).toLocaleString() },
      },
    ],
    yAxis: [{ type: "category", data: yCats }],
    series: [
      { name: "Men", type: "bar", stack: "pop", data: men },
      { name: "Women", type: "bar", stack: "pop", data: women },
    ],
  };
}

/* =========================
   Health Care Expenditure
   ========================= */
function healthCareExpenditure(elements) {
  if (!Array.isArray(elements) || !elements.length) return null;
  const row = elements[0];
  const years = Object.keys(row)
    .filter((k) => /^\d{4}$/.test(String(k)))
    .sort();
  const data = years.map((y) => num(row[y]));
  return {
    title: { text: "Total BE Health Care Expenditure", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    grid: { left: 64, right: 24, top: 56, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value" },
    series: [{ type: "line", data, smooth: true, name: "Total" }],
  };
}

/* =========================
   Immigration grouped bars
   ========================= */
function immigration2020(elements) {
  if (!Array.isArray(elements) || !elements.length) return null;

  const rows = elements.filter(
    (r) =>
      r["Demo Group Type"] === "Country of Birth" &&
      ["EU", "Non-EU Europe", "Non-Europe"].includes(
        String(r["Demographic Group"])
      )
  );

  if (!rows.length) return null;

  const x = rows.map((r) => String(r["Demographic Group"]));
  const women = rows.map((r) => num(r["Women"]));
  const men = rows.map((r) => num(r["Men"]));
  const both = rows.map((r) => num(r["Both"]));

  return {
    title: { text: "Total No. of Immigrants by Region 2020", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { top: 28 },
    grid: { left: 64, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: x },
    yAxis: { type: "value" },
    series: [
      { name: "Women", type: "bar", data: women },
      { name: "Men", type: "bar", data: men },
      { name: "Both", type: "bar", data: both },
    ],
  };
}

/* =========================
   Main Component
   ========================= */
export default function BelgiumDashboard({ metas, dataById, policyData }) {
  const charts = useMemo(() => {
    const out = [];

    // AROPE (5 charts)
    const aropeEls = getElements(metas, dataById, AROPE_ID);
    if (aropeEls.length) {
      const opt1 = applyTextColor(aropeByNUTS2(aropeEls));
      const opt2 = applyTextColor(aropeByGender(aropeEls));
      const opt3 = applyTextColor(aropeByAge(aropeEls));
      const opt4 = applyTextColor(aropeByBirth(aropeEls));
      const opt5 = applyTextColor(aropeByUrbanicity(aropeEls));
      if (opt1) out.push({ key: "be-arope-nuts2", option: opt1 });
      if (opt2) out.push({ key: "be-arope-gender", option: opt2 });
      if (opt3) out.push({ key: "be-arope-age", option: opt3 });
      if (opt4) out.push({ key: "be-arope-birth", option: opt4 });
      if (opt5) out.push({ key: "be-arope-urbanicity", option: opt5 });
    }

    // Age distribution (2 charts)
    const ageEls = getElements(metas, dataById, AGE_ID);
    if (ageEls.length) {
      const optBE = applyTextColor(agePyramid(
        ageEls,
        "Total Belgium",
        "Age Pyramid Belgium 01/01/2025"
      ));
      const optFL = applyTextColor(agePyramid(
        ageEls,
        "Flanders",
        "Age Pyramid Flanders 01/01/2025"
      ));
      if (optBE) out.push({ key: "be-age-be", option: optBE });
      if (optFL) out.push({ key: "be-age-fl", option: optFL });
    }

    // Health Care Expenditure (1 chart)
    const hceEls = getElements(metas, dataById, HCE_ID);
    if (hceEls.length) {
      const opt = applyTextColor(healthCareExpenditure(hceEls));
      if (opt) out.push({ key: "be-hce", option: opt });
    }

    // Immigration (1 chart)
    const immEls = getElements(metas, dataById, IMM_ID);
    if (immEls.length) {
      const opt = applyTextColor(immigration2020(immEls));
      if (opt) out.push({ key: "be-imm", option: opt });
    }

    return out;
  }, [metas, dataById]);

  if (!charts.length && !policyData) return null;

  return (
    <div className="chart-grid">
      {policyData && <ScreeningPolicyCard policyData={policyData} />}
      {charts.map((c) => (
        <ChartCard key={c.key} option={c.option} />
      ))}
    </div>
  );
}
